import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, BookOpen, MoreVertical, Trash2, Edit, LogOut, Scroll, Download, Crown, Shield, ShieldCheck, HelpCircle, Sparkles, Loader2, MapPin, Users, Skull, Package, Link2, Play, Settings, Clock, User, Mail } from "lucide-react";
import ExportCampaign from "@/components/ExportCampaign";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [startWithTemplate, setStartWithTemplate] = useState(false);
  const [templateTheme, setTemplateTheme] = useState("classic");
  const [premadeCampaigns, setPremadeCampaigns] = useState([]);
  const [copyingPremade, setCopyingPremade] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchPremade();
  }, []);

  const fetchPremade = async () => {
    try {
      const res = await axios.get(`${API}/premade-campaigns`, { withCredentials: true });
      setPremadeCampaigns(res.data);
    } catch { /* silent */ }
  };

  const copyPremade = async (premadeId) => {
    setCopyingPremade(premadeId);
    try {
      const res = await axios.post(`${API}/premade-campaigns/${premadeId}/copy`, {}, { withCredentials: true });
      toast.success(`"${res.data.name}" added to your campaigns!`);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to copy campaign");
    } finally {
      setCopyingPremade(null);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData, { withCredentials: true });
        toast.success("Campaign updated");
      } else {
        const res = await axios.post(`${API}/campaigns`, { ...formData, use_template: startWithTemplate, template_theme: templateTheme }, { withCredentials: true });
        toast.success(startWithTemplate ? "Campaign created with a Starter World" : "Campaign created");
      }
      setDialogOpen(false);
      setFormData({ name: "", description: "" });
      setStartWithTemplate(false);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail, {
          action: { label: "Upgrade", onClick: () => navigate("/subscription") }
        });
      } else {
        toast.error("Failed to save campaign");
      }
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Campaign", "Delete this campaign and all its content? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({ name: campaign.name, description: campaign.description || "" });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingCampaign(null);
    setFormData({ name: "", description: "" });
    setStartWithTemplate(false);
    setTemplateTheme("classic");
    setDialogOpen(true);
  };

  /** Copy the permanent player link for a campaign to the clipboard. */
  const copyPlayerLink = async (campaign, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/player-link`, { withCredentials: true });
      const url = `${window.location.origin}/join/${res.data.player_link_code}`;
      await navigator.clipboard.writeText(url);
      toast.success("Player link copied — share it with your table");
    } catch {
      toast.error("Failed to copy player link");
    }
  };

  const openAdmin = (campaign, e) => {
    if (e) e.stopPropagation();
    // Phase 1 — reuse the existing edit-campaign dialog. Phase 8 will bring a
    // full Admin Mode workspace with per-campaign appearance, permissions etc.
    openEdit(campaign);
  };

  const formatUpdated = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const day = 86400000;
      if (diff < day) return "today";
      if (diff < 2 * day) return "yesterday";
      if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-xl font-serif text-foreground tracking-wide">
              NaturalOne<span className="text-primary">-Chronicles</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.name || user?.email}
            </span>
            {user?.is_admin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="text-muted-foreground hover:text-foreground"
                data-testid="admin-panel-link"
              >
                <ShieldCheck className="w-4 h-4 mr-1" /> Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/subscription")}
              className="text-muted-foreground hover:text-foreground"
              data-testid="subscription-link"
            >
              <Crown className="w-4 h-4 mr-1" />
              <span className="capitalize">{user?.tier || "commoner"}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/help")}
              className="text-muted-foreground hover:text-foreground"
              data-testid="help-link"
            >
              <BookOpen className="w-4 h-4 mr-1" /> Help
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/support")}
              className="text-muted-foreground hover:text-foreground"
              data-testid="support-link"
            >
              <HelpCircle className="w-4 h-4 mr-1" /> Support
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-button"
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-serif text-foreground mb-2">Your Campaigns</h2>
            <p className="text-muted-foreground">Select a campaign to begin chronicling your adventure</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openCreate}
                data-testid="create-campaign-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editingCampaign ? "Edit Campaign" : "Create New Campaign"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Campaign Name
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="The Lost Mines of Phandelver"
                    required
                    data-testid="campaign-name-input"
                    className="bg-input border-border rounded-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="A tale of adventure in the Sword Coast..."
                    rows={3}
                    data-testid="campaign-description-input"
                    className="bg-input border-border rounded-sm"
                  />
                </div>
                {!editingCampaign && (
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      How do you want to start?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setStartWithTemplate(false)}
                        data-testid="start-blank-option"
                        className={`text-left rounded-md border p-3 transition-colors ${!startWithTemplate ? "border-primary bg-primary/10" : "border-border bg-input hover:border-primary/40"}`}
                      >
                        <div className="text-sm font-semibold text-foreground">Start blank</div>
                        <div className="text-xs text-muted-foreground mt-0.5">An empty campaign — build everything yourself.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setStartWithTemplate(true)}
                        data-testid="start-template-option"
                        className={`text-left rounded-md border p-3 transition-colors ${startWithTemplate ? "border-primary bg-primary/10" : "border-border bg-input hover:border-primary/40"}`}
                      >
                        <div className="text-sm font-semibold text-foreground flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5 text-primary" /> Starter World
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Prefilled regions, NPCs, deities, quests, lore & a table — all editable.</div>
                      </button>
                    </div>
                    {startWithTemplate && (
                      <div className="mt-2">
                        <label className="text-xs font-medium text-muted-foreground block mb-1.5">Choose a flavour</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { key: "classic", label: "Classic Fantasy", desc: "Villages, ruins & a rising evil" },
                            { key: "horror", label: "Grim Horror", desc: "Fog, dread & things that don't stay dead" },
                            { key: "high_magic", label: "High Magic", desc: "Floating cities & wild arcane storms" },
                          ].map((t) => (
                            <button
                              key={t.key}
                              type="button"
                              onClick={() => setTemplateTheme(t.key)}
                              data-testid={`template-theme-${t.key}`}
                              className={`text-left rounded-md border p-2 transition-colors ${templateTheme === t.key ? "border-primary bg-primary/10" : "border-border bg-input hover:border-primary/40"}`}
                            >
                              <div className="text-xs font-semibold text-foreground">{t.label}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{t.desc}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <Button 
                  type="submit"
                  data-testid="save-campaign-button"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  {editingCampaign ? "Save Changes" : "Create Campaign"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <Scroll className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Start fresh with a blank campaign, or drop in one of our ready-made starter adventures
              with NPCs, locations, lore, and a hook already in place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={openCreate}
                data-testid="empty-create-campaign-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
              {premadeCampaigns.length > 0 && (
                <Button
                  onClick={() => {
                    document.querySelector('[data-testid="premade-campaigns-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  variant="outline"
                  data-testid="empty-try-sample-button"
                  className="rounded-sm border-primary/40 text-primary hover:bg-primary/5"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Try a Starter Adventure ({premadeCampaigns.length})
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="campaign-card bg-card border border-border rounded-sm p-5 relative group flex flex-col"
                data-testid={`campaign-card-${campaign.id}`}
              >
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <ExportCampaign campaignId={campaign.id} campaignName={campaign.name} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`campaign-menu-${campaign.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover border-border">
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openEdit(campaign); }}
                        data-testid={`edit-campaign-${campaign.id}`}
                      >
                        <Edit className="w-4 h-4 mr-2" /> Rename / Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleDelete(campaign.id); }}
                        className="text-destructive"
                        data-testid={`delete-campaign-${campaign.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Campaign
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-serif text-foreground leading-tight truncate" title={campaign.name}>{campaign.name}</h3>
                    {campaign.updated_at && (
                      <p className="text-[11px] text-muted-foreground/70 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Updated {formatUpdated(campaign.updated_at)}
                      </p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
                  {campaign.description || "No description yet."}
                </p>

                {/* Actions */}
                <div className="flex flex-col gap-2 mt-auto">
                  <Button
                    onClick={() => navigate(`/campaign/${campaign.id}`)}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                    data-testid={`open-campaign-${campaign.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" /> Open Campaign
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={(e) => openAdmin(campaign, e)}
                      className="rounded-sm border-border text-xs"
                      data-testid={`admin-campaign-${campaign.id}`}
                    >
                      <Settings className="w-3.5 h-3.5 mr-1" /> Admin
                    </Button>
                    <Button
                      variant="outline"
                      onClick={(e) => copyPlayerLink(campaign, e)}
                      className="rounded-sm border-border text-xs"
                      data-testid={`copy-player-link-${campaign.id}`}
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1" /> Copy Player Link
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Starter Adventures */}
        {premadeCampaigns.length > 0 && (
          <div className="mt-12" data-testid="premade-campaigns-section">
            <div className="mb-6">
              <h2 className="text-2xl font-serif text-foreground mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" /> Starter Adventures
              </h2>
              <p className="text-sm text-muted-foreground">Ready-made campaigns you can add and customise. Free for all tiers.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {premadeCampaigns.map((pm) => (
                <div key={pm.id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`premade-${pm.id}`}>
                  {pm.image_url && (
                    <div className="h-40 overflow-hidden">
                      <img src={pm.image_url} alt={pm.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg font-serif text-foreground mb-2">{pm.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{pm.description}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {pm.chapter_count} Chapters</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {pm.npc_count} NPCs</span>
                      <span className="flex items-center gap-1"><Skull className="w-3 h-3" /> {pm.creature_count} Creatures</span>
                      <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {pm.item_count} Items</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {pm.location_count} Locations</span>
                    </div>
                    <Button
                      onClick={() => copyPremade(pm.id)}
                      disabled={copyingPremade === pm.id}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                      data-testid={`copy-premade-${pm.id}`}
                    >
                      {copyingPremade === pm.id ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Copying...</>
                      ) : (
                        <><Plus className="w-4 h-4 mr-2" /> Add to My Campaigns</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Account Details */}
        <section className="mt-14 border-t border-border pt-8" data-testid="account-details-section">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-foreground">Account Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-sm p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3" /> Signed in as
              </p>
              <p className="text-sm text-foreground truncate">{user?.name || "—"}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <Crown className="w-3 h-3" /> Plan
              </p>
              <p className="text-sm text-foreground capitalize">{user?.tier || "commoner"}</p>
              <button
                onClick={() => navigate("/subscription")}
                className="text-xs text-primary hover:underline mt-1"
                data-testid="manage-plan-link"
              >
                Manage plan &rarr;
              </button>
            </div>
            <div className="bg-card border border-border rounded-sm p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> Campaigns in use
              </p>
              <p className="text-sm text-foreground">
                {campaigns.filter((c) => c.role === "dm").length}
                <span className="text-muted-foreground"> owned</span>
              </p>
              <p className="text-xs text-muted-foreground">
                {campaigns.filter((c) => c.role !== "dm").length > 0 ? `+ ${campaigns.filter((c) => c.role !== "dm").length} as player` : "\u00a0"}
              </p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 flex flex-col justify-between">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Account controls</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={() => navigate("/help")} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">Help</button>
                <button onClick={() => navigate("/support")} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">Support</button>
                <button onClick={logout} className="text-xs text-destructive hover:underline" data-testid="logout-link">Sign out</button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
